import dayjs from "dayjs"
import {
  find,
  first,
  get,
  isArray,
  isEmpty,
  merge,
  sortBy,
  upperFirst,
  uniqWith,
  isEqual,
} from "lodash"

import { useSelector } from "@ggs/store"
import { numberFormatter, truncateString } from "@ggs/utils"
import bodyTrim from "../../helpers/body-trim"
import useUserLocation from "./useUserLocation"

/**
 * @typedef {import("../../definitions").BaseAd} BaseAd
 * @typedef {import("../../definitions").RunOfSiteAd} RunOfSiteAd
 * @typedef {import("../../definitions").TripLevelAd} TripLevelAd
 * @typedef {import("../../definitions").AuthorArticle} AuthorArticle
 * @typedef {import("../../definitions").TrendingArticles} TrendingArticles
 * @typedef {import("../../components/GlobalAdBanners/Types").AdBanner} AdBanner
 * @typedef {import("../../components/LocationBanners/LocationBanners").BannerItem} LocationBanner
 */

/**
 * @typedef {import("../../definitions").SlideshowItemType} SlideshowItemType
 * @typedef {import("../../definitions").BaseStaffProfile} BaseStaffProfile
 */

/**
 * @typedef {import("../../definitions").TeaserGridItemProps} SlideshowItemType
 * @typedef {import("../../definitions").BaseStaffProfile} BaseStaffProfile
 */

/**
 * @typedef {import("../../definitions").Article} ArticleTeaser
 */

/**
 * @typedef {(results: BaseAd[], type: string) => RunOfSiteAd} GlobalSiteAdIndexFilter
 * @typedef {(ads: RunOfSiteAd) => any} RunOfSiteAdsIndexToBanner
 */

/**
 * @typedef {Object} UseDataObjectTransformHook
 * @property {GlobalSiteAdIndexFilter} globalSiteAdIndexFilter
 * @property {RunOfSiteAdsIndexToBanner} runOfSiteAdIndexToAdBanner
 */

/**
 * @typedef {import("../../definitions").CountryDestination} CountryDestination
 */

/**
 * @typedef {import("../../components/BlogSubNavigation/Types").NavigationItemProps} NavigationItem
 * @typedef {import("../../definitions").ArticleCategory} ArticleCategory
 */

/**
 * @typedef {import("../../components/TeaserCard/TeaserCard").TeaserCardProps} TravelGuideItem
 * @typedef {import("../../definitions").TravelGuide} TravelGuide
 */

/**
 * @typedef {import("../../components/TripTeaser/TripTeaser").TripTeaserProps} TripItem
 * @typedef {import("../../definitions").Trip} Trip
 */

const useDataObjectTransform = () => {
  const userCountry = useSelector((state) => state.settings.country)

  // Helper function to convert machine names to friendly names
  const convertMachineNameToFriendly = (name) => {
    if (!name || typeof name !== 'string') return ''

    // Check if it looks like a machine name (lowercase with hyphens/underscores)
    const isMachineName = /^[a-z0-9-_]+$/.test(name) && (name.includes('-') || name.includes('_'))

    if (isMachineName) {
      // Convert machine name to friendly format
      return name
        .split(/[-_]/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    }

    // If it's already a friendly name, return as-is
    return name
  }
  const { mapCountryToCurrency, mapSaveAmountToCurrency } = useUserLocation()
  const currencyCode = mapCountryToCurrency(userCountry?.code) || ""
  const saveAmountField = mapSaveAmountToCurrency(userCountry?.code) || ""
  const saveAmountFieldOverride = mapSaveAmountToCurrency(userCountry?.code, false, true) || ""
  const priceField = `its_package_price_${currencyCode.toLocaleLowerCase()}`
  const priceMaxField = `its_package_max_${currencyCode.toLocaleLowerCase()}`
  const overridePriceField = `package_price_${currencyCode.toLocaleLowerCase()}`

  /**
   * Filter global site ad index result objects by ad type:
   *  - run_of_site_ad
   *  - trip_level_ad
   * @type {GlobalSiteAdIndexFilter}
   * @param {BaseAd[]} results Global site index results array
   * @param {string} type Ad type to filter
   * @returns {RunOfSiteAd}
   */
  const globalSiteAdIndexFilter = (results, type) => {
    if (!isArray(results)) {
      return null
    }

    return results.filter((item) => {
      const componentType = first(item?.sm_components_type)
      return componentType === type
    })
  }

  /**
   * Filter staff result objects by current profile:
   * @param {BaseStaffProfile[]} results Global site index results array
   * @param current_profile_id
   * @returns {SlideshowItemType[]}
   */
  const staffIndexFilterAndTransform = (results, current_profile_id = null) => {
    if (!isArray(results)) {
      return null
    }
    const filteredResults = current_profile_id
      ? results.filter((item) => {
        return item?.its_nid[0] !== current_profile_id
      })
      : results
    if (filteredResults.length > 0) {
      return filteredResults?.map((item) => {
        const imageStylesJson = first(item?.zs_photo_image_style_obj) || "{}"
        const imageStylesArray = JSON.parse(imageStylesJson) || {}
        const imageStyles = isEmpty(imageStylesJson) ? {} : imageStylesArray

        const resolve = {
          title: item.ss_title?.[0],
          subtitle: item.ss_position?.[0],
          uri: item.ss_url?.[0],
          link: {
            title: item.ss_title?.[0],
            uri: item.ss_url?.[0],
          },
        }
        if (imageStylesArray.length > 0) {
          resolve.image = {
            variants: [
              ...imageStylesArray,
              {
                style: "default",
                url:
                  find(imageStyles, "profile_overview_image_3xl").profile_overview_image_3xl || "",
              },
            ],
          }
        }
        return resolve
      })
    }
    return null
  }

  /**
   * Transform staff users to match Slideshow component data model:
   * @param {BaseStaffProfile[]} results Global site index results array
   * @returns {SlideshowItemType[]}
   */
  const staffIndexTransformLandingPage = (results) => {
    if (!isArray(results)) {
      return null
    }
    return results?.map((item) => {
      const imageStylesJson = first(item?.zs_photo_image_style_obj) || "{}"
      const imageStylesArray = JSON.parse(imageStylesJson) || {}
      const imageStyles = isEmpty(imageStylesJson) ? {} : imageStylesArray

      const resolve = {
        title: item.ss_title_s,
        link: {
          title: item.ss_title_s,
          uri: item.ss_url?.[0],
        },
      }

      if (imageStylesArray.length > 0) {
        resolve.image = {
          variants: [
            ...imageStylesArray,
            {
              style: "default",
              url: find(imageStyles, "profile_overview_image_3xl").profile_overview_image_3xl || "",
            },
          ],
        }
      }

      return resolve
    })
  }

  /**
   * Filter staff result objects by current profile:
   * @param {BaseStaffProfile[]} results Global site index results array
   * @returns {DestinationsGridProps[]}
   */
  const destinationFilterAndTransform = (results) => {
    if (!isArray(results)) {
      return null
    }
    if (results.length > 0) {
      return results.map((item) => {
        const imageStylesJson = first(item?.zs_hero_image_style_obj) || "{}"
        const imageStylesArray = JSON.parse(imageStylesJson) || {}
        const imageStyles = isEmpty(imageStylesJson) ? {} : imageStylesArray
        const resolve = {
          link: {
            title: item.ss_title?.[0],
            uri: item?.ss_url?.[0],
          },
        }
        if (imageStylesArray.length > 0) {
          resolve.image = {
            variants: [
              ...imageStylesArray,
              {
                style: "default",
                url:
                  find(imageStyles, "staff_type_image_overview_image_3xl")
                    .staff_type_image_overview_image_3xl || "",
                alt: item?.sm_hero_images_alt,
              },
            ],
          }
        }
        return resolve
      })
    }
    return null
  }

  /**
   * Filter staff result objects by current profile:
   * @param {BaseStaffProfile[]} results Global site index results array
   * @returns {DestinationsGridProps[]}
   */
  const staffIndexTransformFilter = (results) => {
    if (!isArray(results)) {
      return null
    }

    if (results.length > 0) {
      return results.map((item) => {
        const imageStylesJson = get(item, "zs_photo_image_style_obj")
        const imageStylesArray = !isEmpty(imageStylesJson) ? JSON.parse(imageStylesJson) : {}
        const imageStyles = isEmpty(imageStylesJson) ? {} : imageStylesArray
        const resolve = {
          name: item?.ss_title,
          location: item?.sm_destinations_title.join(", "),
          bio: first(item?.ss_bio),
          uri: first(item?.ss_url),
        }

        if (!isEmpty(imageStyles)) {
          resolve.image = {
            variants: [
              ...imageStyles,
              {
                style: "default",
                url: find(imageStyles, "staff_profile_photo_3xl")?.staff_profile_photo_3xl,
              },
              {
                style: "small",
                url: find(imageStyles, "staff_profile_photo_small")?.staff_profile_photo_small,
              },
            ],
          }
        }
        return resolve
      })
    }
    return null
  }

  /**
   * Transform Run of Site Ad results into add banner items.
   * @type {RunOfSiteAdsIndexToBanner}
   * @param {RunOfSiteAd[]} ads
   * @return {AdBanner[]}
   */
  const runOfSiteAdsIndexToAdBanner = (ads) => {
    if (!isArray(ads)) {
      return null
    }

    return ads.map((item) => {
      const imageStylesJson = first(item?.sm_components_image_image_style_obj) || "{}"
      const imageStylesObject = JSON.parse(imageStylesJson) || {}
      const imageStyles = merge({}, ...imageStylesObject)

      return {
        title: item.sm_components_title,
        description: item.sm_components_description,
        link: {
          label: item.sm_components_link_title,
          url: item.sm_components_link_url,
        },
        imageUrl: get(imageStyles, "run_of_site_ad_3xl") || "",
      }
    })
  }

  const fieldCheck = (field) => {
    if (isArray(field) && field.length > 0) return field[0]
  }

  /**
   * General mapping of destination fields to map items objects.
   *
   * @param {CountryDestination[]} destinations
   * @param {string} typeField
   */
  const mapDestinationFields = (destinations, typeField) => {
    return destinations.map((destination) => {
      const imageStylesJson = first(destination?.zs_hero_image_style_obj) || "{}"
      const imageStylesArray = JSON.parse(imageStylesJson) || {}
      const imageStyles = isEmpty(imageStylesJson) ? {} : imageStylesArray

      const resolve = {
        label: fieldCheck(destination?.ss_title),
        description: fieldCheck(destination?.ss_facts_overview_description),
        lat: fieldCheck(destination?.fts_lat),
        lng: fieldCheck(destination?.fts_lng),
        uri: fieldCheck(destination?.ss_url),
        type: upperFirst(fieldCheck(get(destination, typeField, ""))),
      }

      if (imageStylesArray.length > 0) {
        resolve.image = {
          variants: [
            ...imageStylesArray,
            {
              style: "default",
              url: find(imageStyles, "hero_s1_3xl").hero_s1_3xl || "",
            },
          ],
        }
      }

      return resolve
    })
  }

  /**
   * Map throught destinations array to create object for Map component
   *
   * @param {CountryDestination[]} results
   */
  const geoEntitiesDataTransform = (results) => {
    if (!isArray(results)) {
      return null
    }

    if (results.length > 0) {
      // filter results that are missing either lat and lng
      const filteredDestinations = results.filter((item) => item.fts_lat && item.fts_lng)
      return mapDestinationFields(filteredDestinations, "ss_type")
    }
  }

  /**
   * Map throught destinations array to create object for Map component
   *
   * @param {CountryDestination[]} results
   */
  const destinationDataTransform = (results) => {
    if (!isArray(results)) {
      return null
    }

    if (results.length > 0) {
      // filter results that are missing either lat and lng
      const filteredDestinations = results.filter((item) => item.fts_lat && item.fts_lng)
      return mapDestinationFields(filteredDestinations, "ss_destination_region_title")
    }
  }

  const countryByDestinationTransform = (destinations) => {
    if (Array.isArray(destinations) && destinations?.length > 0) {
      // sort destinations alphabetically by title
      const sortedDestinations = sortBy(destinations, "ss_title[0]")
      return sortedDestinations.map((destination) => {
        const imageStylesJson = first(destination?.zs_hero_image_style_obj) || "{}"
        const imageStylesArray = JSON.parse(imageStylesJson) || {}
        const imageStyles = isEmpty(imageStylesJson) ? {} : imageStylesArray

        const resolve = {
          title: destination?.ss_title?.[0],
          link: destination?.ss_url?.[0],
        }

        if (imageStylesArray.length > 0) {
          resolve.image = {
            variants: [
              ...imageStylesArray,
              {
                style: "default",
                url: find(imageStyles, "hero_s1_3xl").hero_s1_3xl || "",
              },
            ],
          }
        }

        return resolve
      })
    }
    return null
  }

  /**
   * Transform Run of Site Ad results into add banner items.
   * @type {RunOfSiteAdsIndexToBanner}
   * @param {TripLevelAd[]} ads
   * @return {LocationBanner[]}
   */
  const locationAdsIndexToLocationBanner = (ads) => {
    if (!isArray(ads)) {
      return null
    }

    const transformed = ads.map((item) => {
      const imageStylesJson = first(item?.sm_components_image_image_style_obj) || "{}"
      const imageStylesObject = JSON.parse(imageStylesJson) || {}
      const imageStyles = merge({}, ...imageStylesObject)
      const imageVariants = []
      const image3xl = {
        url: get(imageStyles, "run_of_site_ad_3xl", null),
        title: first(item.sm_components_title),
        style: "3xl",
        alt: "",
        height: null,
        width: null,
      }

      if (image3xl.url) {
        imageVariants.push(image3xl)
      }

      const countryName = get(item, "sm_country_title[0]", null)
      const destinationName = get(item, "sm_destinations_title[0]", null)
      const locationName =
        countryName && destinationName ? `${countryName} - ${destinationName}` : null

      const result = {
        id: item.id,
        title: first(item.sm_components_title),
        titleLogoUrl: first(item.sm_components_icon_url),
        description: first(item.sm_components_description) || "", // Add fallback for missing description
        link: {
          title: first(item.sm_components_link_title) || "",
          uri: first(item.sm_components_link_url) || "#",
        },
        location: locationName,
        image: {
          variants: imageVariants,
        },
      }

      return result
    })

    return transformed
  }

  /**
   * Transform Article results into article teasers items.
   * @type {RunOfSiteAdsIndexToBanner}
   * @param {ArticleTeaser[]} articles
   */
  const articleTeaserTransform = (articles) => {
    if (!isArray(articles)) {
      return []
    }

    return articles.map((article) => {
      const resolve = {
        article_type: {
          title: get(article, "sm_article_category_title[0]", ""),
          uri: get(article, "sm_article_category_path[0]", "#"),
        },
        title: article.ss_title,
        description: bodyTrim(article.ss_body?.[0], 20),
        uri: article.ss_url?.[0],
        nid: article.its_nid,
        contentType: first(article.ss_type) || "",
      }

      const imageStylesJson = first(article.zm_featured_images_image_style_obj) || "{}"
      const imageStylesArray = JSON.parse(imageStylesJson) || {}
      const imageStyles = isEmpty(imageStylesJson) ? {} : imageStylesArray

      resolve.image = {
        variants: [
          {
            style: "default",
            url:
              find(imageStyles, "article_featured_image_teaser")?.article_featured_image_teaser ||
              "",
            alt: article?.sm_featured_images_alt || "",
          },
        ],
      }

      return resolve
    })
  }

  const articleOverrideTransform = (articles) => {
    if (!isArray(articles)) {
      return []
    }

    return articles.map((item) => ({
      ...item,
      image: item?.featured_images?.[0],
      uri: item?.slug,
    }))
  }

  const getBookDate = (specialOfferScheduler) => {
    if (specialOfferScheduler) {
      const isDateExpired = dayjs().isAfter(dayjs(specialOfferScheduler))
      return isDateExpired ? null : dayjs(specialOfferScheduler).format("MM/DD/YY")
    }
  }

  const tripOverrideTransform = (trips) => {
    if (!isArray(trips)) {
      return []
    }

    return trips.map((trip) => {
      const countryCategories = [
        {
          label: trip?.primary_country?.title,
          uri: trip?.primary_country?.slug,
        },
      ]
      const priceValue = trip?.package_code?.[overridePriceField] || ""
      const priceMaxValue = trip?.package_code?.[priceMaxField] || ""
      const tripLocationsArray = trip?.geo_pages?.map((page) => page?.title) || []
      return {
        ...trip,
        tripCode: trip?.package_code?.p_code,
        primaryCountry: trip?.primary_country?.title,
        destinationSlug: trip?.primary_country?.destinations?.[0]?.slug,
        image: trip?.hero_image,
        description: trip?.intro_text || trip?.trip_overview_description,
        uri: trip?.self_link?.uri,
        specialOfferShortDescription: trip?.short_special_offer_description,
        tripLocations: formatLocationsToString(tripLocationsArray),
        bookDate: getBookDate(trip?.special_offer_scheduler),
        duration: trip?.package_code?.package_duration || -1,
        price: numberFormatter.currency(priceValue, numberFormatter.CURRENCIES[currencyCode]),
        maxPrice: numberFormatter.currency(priceMaxValue, numberFormatter.CURRENCIES[currencyCode]),
        rawPrice: priceValue,
        rawMaxPrice: priceMaxValue,
        savings: resolveTripOverrideSavings(trip),
        addedValue: trip?.added_value,
        isSpecial: trip?.special_offer,
        ...(countryCategories.length > 0
          ? {
            locationTags: countryCategories,
          }
          : {}),
      }
    })
  }

  /**
   * Transform Run of Site Ad results into add banner items.
   * @param {SubPage[]} items
   * @return {links[{title, uri}}]}
   */
  const subPagesIndexToDynamicSubPageItems = (items) => {
    if (!isArray(items)) {
      return null
    }

    const mapPageLabel = (type) => {
      switch (type) {
        case "places_to_go":
          return "Places To Go"
        case "trip_itineraries":
          return "Trip Itineraries"
        case "travel_guides":
          return "Travel Guides"
        default:
          return null
      }
    }

    const transformed = items.map((item) => {
      return {
        title: mapPageLabel(item?.ss_template_type?.[0]) || "",
        uri: item.ss_url?.[0],
      }
    })
    return transformed
  }

  /**
   * Transform author articles for Author Articles Collection.
   * @param {AuthorArticle[]} articles
   * or Trending articles.
   * @param {TrendingArticles[]} articles
   * @param {[key: string]: string} authorfLink
   */

  const authorArticlesCollectionDataFilterAndTransform = (articles, authorfLink, liked) => {
    if (!isArray(articles)) {
      return null
    }

    return articles?.map((article) => {
      const imageStylesJson = first(article.zm_featured_images_image_style_obj) || "{}"
      const imageStylesArray = JSON.parse(imageStylesJson) || {}
      const imageStyles = isEmpty(imageStylesJson) ? {} : imageStylesArray
      const imageStyleObject = find(imageStyles, "article_featured_image_teaser")
      const image = {
        variants: [
          {
            style: "default",
            url: imageStyleObject?.article_featured_image_teaser || "",
          },
        ],
      }

      // Get author title - prioritize authorfLink, then convert machine name to friendly
      const authorSlug = article.ss_authored_by_slug?.[0]
      const authorTitle = authorfLink?.title || convertMachineNameToFriendly(authorSlug) || ""

      return {
        image,
        title: first(article.ss_title) || "",
        author:
          {
            title: authorTitle,
            uri:
              authorfLink?.uri || article.ss_authored_by_path ? article.ss_authored_by_path[0] : "",
          } || {},
        uri: first(article.ss_url) || "",
        date: dayjs(article.ds_created[0]).format("MMM DD, YYYY"),
        tags:
          article.sm_article_category_title?.length > 0
            ? article.sm_article_category_title?.map((category, index) => {
              return {
                label: category,
                color: "floralWhite",
                uri: article.sm_article_category_path?.[index],
              }
            })
            : {},
        contentType: first(article.ss_type) || "",
        nid: first(article.its_nid),
        ...(liked ? { liked } : { liked: false }),
      }
    })
  }

  /** Transform Article category results into blog sub navigation items.
   * @param {ArticleCategory[]} categories
   * @return {NavigationItem[]}
   */
  const articleCategoryTransform = (categories) => {
    if (!isArray(categories)) {
      return null
    }

    return categories?.map((item) => ({
      title: item?.ss_title?.[0],
      uri: item?.ss_url?.[0],
      icon: item?.ss_icon_option?.[0],
      id: item?.its_nid[0],
    }))
  }

  /**
   * @param {TravelGuide[]} travelGuides
   * @return {TravelGuideItem[]}
   */
  const travelGuideFilterAndTransform = (travelGuides, excludedTravelGuideId) => {
    if (!isArray(travelGuides)) return null

    const filteredTravelGuides = travelGuides?.filter((guide) => {
      return guide?.its_nid?.[0] !== excludedTravelGuideId
    })

    if (isArray(filteredTravelGuides) && filteredTravelGuides?.length) {
      return filteredTravelGuides?.map((guide) => {
        const imageStylesJson = first(guide?.zs_hero_image_image_style_obj) || "{}"
        const imageStylesArray = JSON.parse(imageStylesJson) || {}
        const imageStyles = isEmpty(imageStylesJson) ? {} : imageStylesArray

        const resolve = {
          title: guide?.ss_title?.[0],
          description: guide?.ss_body?.[0],
          link: { title: "Read Guide", uri: guide?.ss_url?.[0] },
          icon: "guide",
        }

        if (imageStylesArray.length > 0) {
          resolve.image = {
            variants: [
              ...imageStylesArray,
              {
                style: "default",
                url: find(imageStyles, "hero_s1_3xl").hero_s1_3xl || "",
              },
            ],
          }
        }
        return resolve
      })
    }
    return null
  }

  /**
   * @param {TravelGuide[]} travelGuides
   * @return {TravelGuideItem[]}
   */
  const nextTravelGuideTransform = (travelGuides) => {
    if (isArray(travelGuides) && travelGuides?.length) {
      const guide = travelGuides[0]
      const imageStylesJson = first(guide?.zs_hero_image_image_style_obj) || "{}"
      const imageStylesArray = JSON.parse(imageStylesJson) || {}
      const imageStyles = isEmpty(imageStylesJson) ? {} : imageStylesArray

      const resolve = {
        title: guide?.ss_title?.[0],
        uri: guide?.ss_url?.[0],
      }
      if (imageStylesArray.length > 0) {
        resolve.image = {
          variants: [
            ...imageStylesArray,
            {
              style: "default",
              url: find(imageStyles, "explore_list_item_3xl").explore_list_item_3xl || "",
            },
          ],
        }
      }
      return resolve
    }
    return null
  }

  /**
   * Transform articles type for Press Centre List.
   * @param {AuthorArticle[]} articles
   */

  const articleTypeDataTransform = (articles) => {
    return articles.map((article) => {
      const image = {
        variants:
          article.zm_featured_images_image_style_obj?.length > 0
            ? Object.values(JSON.parse(article.zm_featured_images_image_style_obj[0])).map(
              (image) => {
                const imageUrl = Object.values(image)
                return {
                  url: imageUrl[0],
                  title: imageUrl[0],
                  alt: imageUrl[0],
                  style: "default",
                }
              }
            )
            : {},
      }
      return {
        title: first(article.ss_title),
        uri: first(article.ss_url),
        date: dayjs(article.ds_created[0]).format("MMM DD, YYYY"),
        image,
      }
    })
  }

  /**
   * @param {TravelGuide[]} travelGuides
   * @return {TravelGuideItem[]}
   */
  const wishlistGuideGuideTransform = (travelGuides) => {
    if (!isArray(travelGuides)) return null
    return travelGuides?.map((guide) => {
      const imageStylesJson = first(guide?.zs_hero_image_image_style_obj) || "{}"
      const imageStylesArray = JSON.parse(imageStylesJson) || {}
      const imageStyles = isEmpty(imageStylesJson) ? {} : imageStylesArray

      const resolve = {
        title: guide?.ss_title?.[0],
        description: guide?.ss_body?.[0],
        link: { title: "Read Guide", uri: guide?.ss_url?.[0] },
        icon: "guide",
        liked: true,
      }

      if (imageStylesArray.length > 0) {
        resolve.image = {
          variants: [
            ...imageStylesArray,
            {
              style: "default",
              url: find(imageStyles, "explore_list_item_3xl").explore_list_item_3xl || "",
            },
          ],
        }
      }
      return resolve
    })
  }

  /**
   * Resolve trip savings for a trip.
   * @param {*} trip
   * @param {string} countryCode
   * @returns {string}
   */
  const resolveTripSavings = (trip) => {
    const saveValue =
      trip[saveAmountField] && trip[saveAmountField][0] ? trip[saveAmountField][0] : ""
    const saveAmount = saveValue
      ? numberFormatter.currency(saveValue, numberFormatter.CURRENCIES[currencyCode])
      : ""
    const savePercent =
      trip.its_save_percent && trip.its_save_percent[0] ? `${trip.its_save_percent[0]}%` : ""

    return saveAmount || savePercent || ""
  }

  const resolveTripOverrideSavings = (trip) => {
    const saveValue =
      trip[saveAmountFieldOverride] && trip[saveAmountFieldOverride]
        ? trip[saveAmountFieldOverride]
        : ""
    const saveAmount = saveValue
      ? numberFormatter.currency(saveValue, numberFormatter.CURRENCIES[currencyCode])
      : ""
    const savePercent = trip.save_percent ? `${trip.save_percent}%` : ""

    return saveAmount || savePercent || ""
  }

  /**
   * Formats an array of locations into a readable string with styled bullet points
   * @param {string[]} locations Array of location strings
   * @returns {string} Formatted string with HTML spans (e.g. "Venice<span class='separator'> • </span>Paris")
   */
  const formatLocationsToString = (locations) => {
    if (!locations || !locations.length) return ""
    if (locations.length === 1) return locations[0]

    let result = locations[0]
    let charCount = locations[0].length

    for (let i = 1; i < locations.length; i++) {
      const location = locations[i]
      const addition = `<span class="location-separator"> • </span>${location}`

      // Check if adding this location would exceed 300 chars
      if (charCount + addition.length > 300) {
        return result + " and more"
      }

      result += addition
      charCount += addition.length
    }

    return result
  }

  const tripDataFilterAndTransform = (trips) => {
    if (!isArray(trips)) return []
    return trips.map((trip) => {
      const imageStylesJson = first(trip?.zs_hero_image_fid_image_styles) || "{}"
      const imageStylesArray = JSON.parse(imageStylesJson) || {}
      const imageStyles = isEmpty(imageStylesJson) ? {} : imageStylesArray

      const image = isEmpty(imageStylesJson)
        ? { variants: [] }
        : {
          variants: [
            {
              style: "default",
              url: find(imageStyles, "hero_s1_teaser_20_23_3xl")?.hero_s1_teaser_20_23_3xl || "",
              alt: trip?.ss_hero_image_alternative_text
                ? trip?.ss_hero_image_alternative_text[0]
                : "",
            },
          ],
        }
      const priceValue = trip?.[priceField]?.[0] || ""
      const priceMaxValue = trip?.[priceMaxField]?.[0] || ""
      const categoryTagsArr = []
      /** Map alias to category.
       * @param {string[]} category
       * @param {string[]} aliases
       */
      const pathHandler = (category, aliases) => {
        category.forEach((item, index) => {
          categoryTagsArr.push({
            label: item || "",
            uri: aliases[index],
            color: "floralWhite",
          })
        })
      }
      trip.sm_ways_to_travel_title &&
        pathHandler(trip.sm_ways_to_travel_title, trip.sm_ways_to_travel_path)
      trip.sm_travel_style_title &&
        pathHandler(trip.sm_travel_style_title, trip.sm_travel_style_path)

      const primaryTagTitle = first(trip.ss_primary_tag_title)

      return {
        external_id: trip.its_nid?.[0],
        title: first(trip.ss_title) || "",
        description:
          first(trip.ss_intro_text) || first(trip.tm_X3b_en_trip_overview_description) || "",
        uri: first(trip.ss_url) || "",
        bookDate: getBookDate(trip?.ds_special_offer_scheduler?.[0]),
        duration: first(trip.its_duration) || 0,
        price: numberFormatter.currency(priceValue, numberFormatter.CURRENCIES[currencyCode]),
        maxPrice: numberFormatter.currency(priceMaxValue, numberFormatter.CURRENCIES[currencyCode]),
        rawPrice: priceValue,
        rawMaxPrice: priceMaxValue,
        savings: resolveTripSavings(trip),
        addedValue: first(trip.bs_added_value) || null,
        isSpecial: first(trip.bs_special_offer) || null,
        specialOfferShortDescription: first(trip.ss_short_special_offer_description),
        tripLocations: formatLocationsToString(trip.sm_geo_pages_title),
        primaryCountry: first(trip.ss_primary_country_country_title),
        destinationSlug: first(trip.ss_primary_country_country_path),
        featured_highlights: first(trip.tm_X3b_en_featured_highlights) || first(trip.ss_featured_highlights) || "",
        primary_tag: primaryTagTitle ? { title: primaryTagTitle } : undefined,
        tripCategories: categoryTagsArr,
        image,
      }
    })
  }

  /**
   * @param {Trip[]} trips
   * @return {TripItem[]}
   */
  const tripSearchFilterAndTransform = (trips) => {
    if (!isArray(trips)) return []

    return trips.map((trip) => {
      const imageStylesJson = first(trip.zs_hero_image_fid_image_styles) || "[]"

      const imageStyles = JSON.parse(imageStylesJson).map((item) => ({
        style: Object.entries(item)[0][0],
        url: Object.entries(item)[0][1],
        alt: trip?.ss_hero_image_alternative_text ? trip?.ss_hero_image_alternative_text[0] : "",
      }))

      const defaultImage = {
        style: "default",
        url: trip.sm_hero_image_url
          ? trip.sm_hero_image_url[0]
          : imageStyles.find((item) => item.style === "hero_s1_3xl")?.url ?? "",
        alt: trip?.ss_hero_image_alternative_text ? trip?.ss_hero_image_alternative_text[0] : "",
      }

      const priceValue = trip?.[priceField]?.[0] || ""
      const priceMaxValue = trip?.[priceMaxField]?.[0] || ""
      const primaryTagTitle = first(trip.ss_primary_tag_title)

      return {
        external_id: first(trip.its_nid) || "",
        title: first(trip.ss_title) || "",
        description:
          first(trip.ss_intro_text) || first(trip.tm_X3b_en_trip_overview_description) || "",
        uri: first(trip.ss_url) || "",
        bookDate: getBookDate(trip?.ds_special_offer_scheduler?.[0]),
        duration: first(trip.its_duration) || 0,
        price: numberFormatter.currency(priceValue, numberFormatter.CURRENCIES[currencyCode]),
        maxPrice: numberFormatter.currency(priceMaxValue, numberFormatter.CURRENCIES[currencyCode]),
        rawPrice: priceValue,
        rawMaxPrice: priceMaxValue,
        savings: resolveTripSavings(trip),
        addedValue: first(trip.bs_added_value) || null,
        isSpecial: first(trip.bs_special_offer) || null,
        specialOfferShortDescription: first(trip.ss_short_special_offer_description),
        tripLocations: formatLocationsToString(trip.sm_geo_pages_title),
        primaryCountry: first(trip.ss_primary_country_country_title),
        destinationSlug: first(trip.ss_primary_country_country_path),
        featured_highlights: first(trip.tm_X3b_en_featured_highlights) || first(trip.ss_featured_highlights) || "",
        primary_tag: primaryTagTitle ? { title: primaryTagTitle } : undefined,
        image: {
          variants: [...imageStyles, defaultImage],
        },
      }
    })
  }

  /**
   * @param {Trip[]} trips
   * @return {TripItem[]}
   */
  const tripSearchFilterAndTransformByCountry = (trips) => {
    if (!isArray(trips)) return []

    return trips.map((trip) => {
      const imageStylesJson = first(trip.zs_hero_image_fid_image_styles) || "[]"

      const imageStyles = JSON.parse(imageStylesJson).map((item) => ({
        style: Object.entries(item)[0][0],
        url: Object.entries(item)[0][1],
        alt: trip?.ss_hero_image_alternative_text ? trip?.ss_hero_image_alternative_text[0] : "",
      }))

      const defaultImage = {
        style: "default",
        url: trip.sm_hero_image_url
          ? trip.sm_hero_image_url[0]
          : imageStyles.find((item) => item.style === "hero_s1_3xl")?.url ?? "",
        alt: trip?.ss_hero_image_alternative_text ? trip?.ss_hero_image_alternative_text[0] : "",
      }

      const priceValue = trip?.[priceField]?.[0] || ""
      const priceMaxValue = trip?.[priceMaxField]?.[0] || ""
      const primaryTagTitle = first(trip.ss_primary_tag_title)

      return {
        external_id: first(trip.its_nid) || "",
        title: first(trip.ss_title) || "",
        description:
          first(trip.ss_intro_text) || first(trip.tm_X3b_en_trip_overview_description) || "",
        uri: first(trip.ss_url) || "",
        bookDate: getBookDate(trip?.ds_special_offer_scheduler?.[0]),
        duration: first(trip.its_duration) || 0,
        price: numberFormatter.currency(priceValue, numberFormatter.CURRENCIES[currencyCode]),
        maxPrice: numberFormatter.currency(priceMaxValue, numberFormatter.CURRENCIES[currencyCode]),
        rawPrice: priceValue,
        rawMaxPrice: priceMaxValue,
        savings: resolveTripSavings(trip),
        addedValue: first(trip.bs_added_value) || null,
        isSpecial: first(trip.bs_special_offer) || null,
        specialOfferShortDescription: first(trip.ss_short_special_offer_description),
        tripLocations: formatLocationsToString(trip.sm_geo_pages_title),
        primaryCountry: first(trip.ss_primary_country_country_title),
        destinationSlug: first(trip.ss_primary_country_country_path),
        featured_highlights: first(trip.tm_X3b_en_featured_highlights) || first(trip.ss_featured_highlights) || "",
        primary_tag: primaryTagTitle ? { title: primaryTagTitle } : undefined,
        image: {
          variants: [...imageStyles, defaultImage],
        },
      }
    })
  }

  /**
   * @param {(string | number)[]} facets
   * @return {{ label: string, value: string }[]}
   */
  const tripSearchFacetLabelValueTransform = (facets, type = "") => {
    if (!isArray(facets)) return []
    const transformedFacets = facets
      .filter((_, i) => i % 2 === 0)
      .map((facet, i) => {
        const facetParts = facet.split(":")
        const [value, label] = facetParts
        return {
          label: label || value,
          value: value,
          ...(type && { experience: type }),
        }
      })
      .sort((a, b) => a.label?.localeCompare(b.label))

    // Deduplicate by label to prevent duplicate entries in autocomplete
    return uniqWith(transformedFacets, (a, b) => a.label === b.label)
  }

  /**
   * @param {(string | number)[]} facets
   * @return {{ label: string, value: string, type }[]}
   */
  const tripSearchFacetLocationTransform = (facets) => {
    if (!isArray(facets)) return []
    const locations = []

    facets
      .filter((_, i) => i % 2 === 0)
      .forEach((facet) => {
        const facetParts = facet.split("_")
        const [destination, country] = facetParts
        const [destinationValue, destinationLabel] = destination.split(":")
        const [countryValue, countryLabel] = country.split(":")
        locations.push({
          label: destinationLabel || destinationValue,
          value: destinationValue,
          type: "destination",
        })

        locations.push({
          label: countryLabel || countryValue,
          value: countryValue,
          type: "country",
        })
      })

    return uniqWith(
      locations.sort((a, b) => a.label?.localeCompare(b.label)),
      isEqual
    )
  }

  /**
   * Transform Search data.
   * @param {GlobalSearchResults[]} data
   */

  const globalSearchDataFilterAndtransform = (data) => {
    if (!isArray(data)) return []
    return data.map((item) => {
      let teaserImage
      if (item.zm_node_featured_images_fid_image_styles)
        teaserImage = item.zm_node_featured_images_fid_image_styles
      if (item.zs_node_photo_fid_image_styles) teaserImage = item.zs_node_photo_fid_image_styles
      if (item.zm_node_hero_fid_image_styles) teaserImage = item.zm_node_hero_fid_image_styles
      if (item.zs_node_hero_image_fid_image_styles)
        teaserImage = item.zs_node_hero_image_fid_image_styles
      if (item.zs_node_featured_images_fid_image_styles)
        teaserImage = item.zs_node_featured_images_fid_image_styles

      const image = {
        variants: teaserImage
          ? Object.values(JSON.parse(teaserImage[0])).map((image) => {
            const imageUrl = Object.values(image)
            return {
              url: imageUrl[0],
              title: imageUrl[0],
              alt: imageUrl[0],
              style: "default",
            }
          })
          : {},
      }

      const description =
        first(item.tm_X3b_en_node_body) ||
        first(item.tm_X3b_en_node_description) ||
        first(item.ss_content_highlight_description) ||
        first(item.tcngramstringm_X3b_en_node_overview_description) ||
        ""
      const cleanDescription = description.replace(/(<([^>]+)>)/gi, "") || ""
      const truncatedDescription = truncateString(cleanDescription, 80)
      const type = first(item.ss_node_type) || ""
      const formattedType = (type) => {
        switch (type) {
          case "trip":
            return "Trip"
          case "destination":
            return "Destination"
          case "article":
          case "travel_guide":
            return "Article"
          case "landing_page":
            return "Other"
          default:
            return type
        }
      }

      return {
        title: first(item.tcngramstringm_X3b_en_node_title) || "",
        description: truncatedDescription,
        image: image || {},
        uri: first(item.ss_url_string) || "",
        type: formattedType(type),
      }
    })
  }

  const autocompleteDataTransform = (data) => {
    if (!isArray(data)) return []
    return data.map((item) => {
      return {
        title: first(item.tcngramstringm_X3b_en_node_title, ""),
        uri: first(item.ss_url, null),
      }
    })
  }

  const articleSearchFilterAndTransform = (articles) => {
    if (!isArray(articles)) return []

    return articles.map((article) => {
      const imageStylesJson = first(article.zs_featured_images_image_style_obj) || "[]"

      const imageStyles = JSON.parse(imageStylesJson).map((item) => ({
        style: Object.entries(item)[0][0],
        url: Object.entries(item)[0][1],
        alt: article?.sm_featured_images_alt || "",
      }))

      // Search results display in a list view so we only neead teaser image.
      const teaserVariant = find(imageStyles, { style: "article_featured_image_teaser" })
      const defaultImage = teaserVariant
        ? [
          {
            ...teaserVariant,
            style: "default",
            alt: article?.sm_featured_images_alt ? article?.sm_featured_images_alt[0] : "",
          },
        ]
        : []

      // Get author title - convert machine name to friendly if needed
      const authorSlug = article.ss_authored_by_slug?.[0]
      const authorTitle = convertMachineNameToFriendly(authorSlug) || ""

      return {
        title: article.ss_title[0],
        image: {
          variants: defaultImage,
        },
        article_type: {
          title: article.sm_article_category_title?.[0],
          uri: article.sm_article_category_path?.[0],
        },
        date: dayjs(article.ds_created?.[0]).format("MMM DD, YYYY"),
        uri: article.ss_url?.[0],
        contentType: article.ss_type?.[0],
        nid: article.its_nid?.[0],
        author: {
          title: authorTitle,
          uri: article.ss_authored_by_path?.[0],
        },
        tags: [
          {
            label: article.sm_article_category_title?.[0],
            uri: article.sm_article_category_path?.[0],
          },
        ],
      }
    })
  }

  const globalSearchFiltersTransform = (filters, totalCount) => {
    if (!isArray(filters)) return []
    const order = ["trip", "destination", "article", "landing_page", "all"]
    const allResultsLFilter = { label: "All", value: "all", count: totalCount }
    const newArray = []
    for (let i = 0; i < filters.length; i += 2) {
      const label = filters[i]
      const count = filters[i + 1]

      // Modify the label to the desired format
      const formattedLabel = () => {
        switch (label) {
          case "landing_page":
            return "Other"
          default:
            return String(label).charAt(0).toUpperCase() + String(label).slice(1)
        }
      }

      // Add the new object to the newArray unless its "travel_guide"
      if (label !== "travel_guide") {
        newArray.push({
          label: formattedLabel(),
          value: label,
          count,
        })
      }
    }
    // return the array sorted in the same order every time
    const formattedArray = newArray.sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value))
    return [...formattedArray, allResultsLFilter]
  }

  const countryBasedPagesDataTransform = (data) => {
    if (!isArray(data)) return []

    const travelInformationMenuItems = data.map((item) => {
      return {
        title: first(item.ss_title),
        uri: first(item.ss_url),
      }
    })

    return travelInformationMenuItems
  }

  const experienceFacetsTransform = (facets) => {
    if (!facets) return { data: [] }

    const processFacetArray = (arr, type) => {
      if (!Array.isArray(arr)) return []

      return arr.reduce((acc, value, index) => {
        // Skip count values which are at odd indices
        if (index % 2 === 1) return acc

        const [id, name] = value.split(":")
        if (!id || !name) return acc

        acc.push({ id, name, type })
        return acc
      }, [])
    }

    const tripTypes = processFacetArray(facets.ss_gw_ways_to_travel_keyvalue_str, "tripTypes")
    const tripStyles = processFacetArray(facets.ss_gw_travel_style_keyvalue_str, "tripStyles")

    return {
      data: [...tripTypes, ...tripStyles],
    }
  }

  const promoDataFilterAndTransform = (data) => {
    if (!isArray(data)) return null
    return data.length > 0
      ? {
        countryId: first(data[0].its_country),
        countryTitle: first(data[0].sm_country_title),
        title: first(data[0].ss_title),
        body: first(data[0].tm_X3b_en_body),
        campaignName: first(data[0].ss_campaign_name),
        uri: first(data[0].ss_url),
      }
      : null
  }

  return {
    globalSiteAdIndexFilter,
    runOfSiteAdsIndexToAdBanner,
    staffIndexFilterAndTransform,
    destinationFilterAndTransform,
    staffIndexTransformFilter,
    destinationDataTransform,
    staffIndexTransformLandingPage,
    locationAdsIndexToLocationBanner,
    articleTeaserTransform,
    authorArticlesCollectionDataFilterAndTransform,
    subPagesIndexToDynamicSubPageItems,
    articleCategoryTransform,
    travelGuideFilterAndTransform,
    nextTravelGuideTransform,
    articleTypeDataTransform,
    tripDataFilterAndTransform,
    countryByDestinationTransform,
    wishlistGuideGuideTransform,
    geoEntitiesDataTransform,
    globalSearchDataFilterAndtransform,
    tripSearchFilterAndTransform,
    tripSearchFacetLabelValueTransform,
    tripSearchFacetLocationTransform,
    tripSearchFilterAndTransformByCountry,
    articleSearchFilterAndTransform,
    articleOverrideTransform,
    tripOverrideTransform,
    autocompleteDataTransform,
    globalSearchFiltersTransform,
    countryBasedPagesDataTransform,
    experienceFacetsTransform,
    promoDataFilterAndTransform,
  }
}

export default useDataObjectTransform
